import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';

export default function AdminAccountSettings() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pw.current_password || !pw.new_password) {
      toast.error('Lengkapi semua field');
      return;
    }
    if (pw.new_password.length < 6) {
      toast.error('Password baru minimal 6 karakter');
      return;
    }
    if (pw.new_password !== pw.confirm_password) {
      toast.error('Konfirmasi password tidak cocok');
      return;
    }
    if (pw.new_password === pw.current_password) {
      toast.error('Password baru harus berbeda dari yang lama');
      return;
    }
    setSaving(true);
    try {
      await api.put('/auth/me/password', {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success('Password berhasil diubah');
      setPw({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-account-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Akun Saya</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola informasi akun dan keamanan login Anda.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#2D6CDF]" /> Informasi Akun
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-slate-500">Nama</p>
              <p className="font-medium text-slate-900">{user?.name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Email</p>
              <p className="font-medium text-slate-900 break-all">{user?.email || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Role</p>
              <p className="font-medium text-slate-900 capitalize">{(user?.role || '').replace('_', ' ')}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound size={18} className="text-violet-500" /> Ubah Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="curr-pw">Password Saat Ini</Label>
                <Input
                  id="curr-pw"
                  type="password"
                  autoComplete="current-password"
                  value={pw.current_password}
                  onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
                  className="mt-1"
                  data-testid="admin-curr-pw-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-pw">Password Baru</Label>
                <Input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={pw.new_password}
                  onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
                  placeholder="Minimal 6 karakter"
                  className="mt-1"
                  data-testid="admin-new-pw-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="conf-pw">Konfirmasi Password Baru</Label>
                <Input
                  id="conf-pw"
                  type="password"
                  autoComplete="new-password"
                  value={pw.confirm_password}
                  onChange={(e) => setPw((p) => ({ ...p, confirm_password: e.target.value }))}
                  className="mt-1"
                  data-testid="admin-conf-pw-input"
                  required
                />
              </div>
              <Button type="submit" disabled={saving} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="admin-save-pw-btn">
                {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Menyimpan...</> : 'Simpan Password Baru'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
